/**
 * AI Master Data Page — product catalog import + AI enrichment
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert,
  Chip, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Breadcrumbs, Link, LinearProgress, IconButton, Tooltip, Avatar,
  TablePagination, InputAdornment,
} from '@mui/material';
import {
  Home as HomeIcon,
  Storage as CatalogIcon,
  Upload as UploadIcon,
  AutoAwesome as EnrichIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  Radar as RadarIcon,
  CheckCircleOutline as AckIcon,
  Calculate as DutyIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  InfoOutlined as InfoIcon,
  Inventory2Outlined as TotalIcon,
  TaskAltOutlined as EnrichedIcon,
  WarningAmberOutlined as ReviewIcon,
  GppMaybeOutlined as DualUseIcon,
  NotificationsActiveOutlined as AlertsIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  importCatalog,
  listMasterDataItems,
  enrichItem,
  enrichItemsBulk,
  updateItem,
  deleteItem,
  createItem,
  getStats,
  exportCatalog,
  bulkSetStatus,
  bulkDeleteItems,
  bulkCommand,
  getAlerts,
  acknowledgeAlert,
  runRadar,
  resetClassifierLearning,
  estimateDuty,
  MasterDataItem,
  MasterDataStats,
  ComplianceAlert,
  DutyEstimate,
} from '../../../services/masterDataService';

const statusColor = (status: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (status) {
    case 'enriched': return 'success';
    case 'confirmed': return 'success';
    case 'needs_review': return 'warning';
    case 'duplicate': return 'default';
    default: return 'info';
  }
};

// Friendly label for where an auto-harvested product came from.
const SOURCE_LABELS: Record<string, string> = {
  declaration: 'From declaration',
  tariff_classifier: 'From classifier',
  export_control: 'From export control',
};
const sourceLabel = (module?: string): string | null =>
  module && SOURCE_LABELS[module] ? SOURCE_LABELS[module] : null;

// Color-coded confidence: green ≥80%, amber 50–79%, red <50%.
const confidenceChip = (conf?: number | null) => {
  if (conf == null) return <>-</>;
  const pct = Math.round(conf * 100);
  const color: 'success' | 'warning' | 'error' = pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error';
  return <Chip label={`${pct}%`} size="small" color={color} variant="outlined" />;
};

const MasterDataPage: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<MasterDataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [enrichingId, setEnrichingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [editItem, setEditItem] = useState<MasterDataItem | null>(null);
  const [editHsCode, setEditHsCode] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState<MasterDataStats | null>(null);
  const [exporting, setExporting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [newSku, setNewSku] = useState('');
  const [newSupplier, setNewSupplier] = useState('');
  const [adding, setAdding] = useState(false);
  const [command, setCommand] = useState('');
  const [running, setRunning] = useState(false);
  const [alerts, setAlerts] = useState<ComplianceAlert[]>([]);
  const [radarRunning, setRadarRunning] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dutyItem, setDutyItem] = useState<MasterDataItem | null>(null);
  const [dutyValue, setDutyValue] = useState('');
  const [dutyLoading, setDutyLoading] = useState(false);
  const [dutyResult, setDutyResult] = useState<DutyEstimate | null>(null);
  const [reasoningItem, setReasoningItem] = useState<MasterDataItem | null>(null);

  // Client-side search + status filter over the fully-loaded catalog.
  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((i) => {
      if (statusFilter === 'dual_use') {
        if (i.is_dual_use !== true) return false;
      } else if (statusFilter !== 'all') {
        if (i.status !== statusFilter) return false;
      }
      if (!q) return true;
      return [i.sku, i.supplier_name, i.raw_description, i.enriched_description, i.hs_code]
        .some((f) => f && f.toLowerCase().includes(q));
    });
  }, [items, search, statusFilter]);

  useEffect(() => {
    loadItems();
    loadStats();
    loadAlerts();
  }, []);

  // Jump back to the first page whenever the search/filter narrows the list.
  useEffect(() => { setPage(0); }, [search, statusFilter]);

  // Keep the current page in range when the visible list shrinks (deletes/filters).
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredItems.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [filteredItems.length, rowsPerPage, page]);

  const loadAlerts = async () => {
    try {
      setAlerts(await getAlerts('open'));
    } catch {
      // radar alerts are supplementary — don't block the page
    }
  };

  const handleRunRadar = async () => {
    setRadarRunning(true);
    try {
      const result = await runRadar();
      toast.success(
        result.new_alerts > 0
          ? `Radar found ${result.new_alerts} new alert(s)`
          : 'Radar scan complete — no new issues'
      );
      loadAlerts();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Radar scan failed');
    } finally {
      setRadarRunning(false);
    }
  };

  const handleResetLearning = async () => {
    if (!window.confirm(
      'Reset ALL learned tariff-classifier corrections? This reverts the classifier '
      + 'to its base model (undoing any HS codes it "learned" from confirmations). '
      + 'Use this to un-poison the classifier.'
    )) return;
    try {
      const result = await resetClassifierLearning();
      toast.success(`Reset ${result.superseded} learned correction(s). Classifier reverted to base model.`);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };

  const handleAcknowledge = async (id: string) => {
    try {
      await acknowledgeAlert(id);
      setAlerts(prev => prev.filter(a => a.id !== id));
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to acknowledge');
    }
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      // The API caps each response at `limit` rows, so a single call silently
      // dropped everything past the first page. Page through until a short page
      // is returned so the whole catalog is loaded (safety-capped to avoid a
      // runaway fetch on a pathologically large catalog).
      const PAGE = 200;
      const MAX_ROWS = 10000;
      const all: MasterDataItem[] = [];
      for (let skip = 0; skip < MAX_ROWS; skip += PAGE) {
        const batch = await listMasterDataItems(undefined, skip, PAGE);
        all.push(...batch);
        if (batch.length < PAGE) break;
      }
      if (all.length >= MAX_ROWS) {
        toast.warn(`Showing the first ${MAX_ROWS} products — narrow your catalog to see more.`);
      }
      setItems(all);
      setPage(0);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load catalog');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      setStats(await getStats());
    } catch {
      // stats are supplementary — don't block the page on a stats failure
    }
  };

  const handleExport = async (format: 'csv' | 'excel') => {
    setExporting(true);
    try {
      await exportCatalog(format);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleAddProduct = async () => {
    if (!newDescription.trim()) {
      toast.error('Description is required');
      return;
    }
    setAdding(true);
    try {
      await createItem({
        raw_description: newDescription.trim(),
        sku: newSku.trim() || undefined,
        supplier_name: newSupplier.trim() || undefined,
      });
      toast.success('Product added');
      setAddOpen(false);
      setNewDescription('');
      setNewSku('');
      setNewSupplier('');
      loadItems();
      loadStats();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to add product');
    } finally {
      setAdding(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await importCatalog(file);
      toast.success(
        `Imported ${result.imported} product(s)` +
        (result.duplicates > 0 ? ` (${result.duplicates} duplicate(s) skipped)` : '')
      );
      loadItems();
      loadStats();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleEnrichOne = async (id: string) => {
    setEnrichingId(id);
    try {
      const updated = await enrichItem(id);
      setItems(prev => prev.map(i => i.id === id ? updated : i));
      loadStats();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Enrichment failed');
    } finally {
      setEnrichingId(null);
    }
  };

  const handleBulkEnrich = async () => {
    setBulkEnriching(true);
    try {
      const result = await enrichItemsBulk(Array.from(selectedIds));
      toast.success(
        `${result.enriched} enriched, ${result.needs_review} need review` +
        (result.failed > 0 ? `, ${result.failed} failed` : '') +
        (result.skipped ? `, ${result.skipped} already done (skipped)` : '')
      );
      setSelectedIds(new Set());
      loadItems();
      loadStats();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Bulk enrichment failed');
    } finally {
      setBulkEnriching(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteItem(id);
      setItems(prev => prev.filter(i => i.id !== id));
      loadStats();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleBulkConfirm = async () => {
    const ids = Array.from(selectedIds) as string[];
    try {
      const result = await bulkSetStatus(ids, 'confirmed');
      toast.success(`${result.updated} product(s) confirmed`);
      setSelectedIds(new Set());
      loadItems();
      loadStats();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Bulk confirm failed');
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds) as string[];
    try {
      const result = await bulkDeleteItems(ids);
      toast.success(`${result.deleted} product(s) deleted`);
      setSelectedIds(new Set());
      loadItems();
      loadStats();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    }
  };

  const handleRunCommand = async () => {
    if (!command.trim()) return;
    const ids = Array.from(selectedIds) as string[];
    setRunning(true);
    try {
      const result = await bulkCommand(ids, command.trim());
      if (result.op === 'unknown') {
        toast.warning(`Could not interpret that command. ${result.explanation || ''}`);
      } else {
        toast.success(`${result.op}: ${result.affected} product(s) affected`);
        setCommand('');
        setSelectedIds(new Set());
        loadItems();
        loadStats();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Command failed');
    } finally {
      setRunning(false);
    }
  };

  const openEdit = (item: MasterDataItem) => {
    setEditItem(item);
    setEditHsCode(item.hs_code || '');
    setEditDescription(item.enriched_description || '');
  };

  const openDuty = (item: MasterDataItem) => {
    setDutyItem(item);
    setDutyValue('');
    setDutyResult(null);
  };

  const handleEstimateDuty = async () => {
    if (!dutyItem) return;
    const value = parseFloat(dutyValue);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error('Enter a customs value greater than 0');
      return;
    }
    setDutyLoading(true);
    try {
      const result = await estimateDuty(dutyItem.id, value);
      setDutyResult(result);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Duty estimate failed');
    } finally {
      setDutyLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      const updated = await updateItem(editItem.id, {
        hs_code: editHsCode,
        enriched_description: editDescription,
        status: 'confirmed',
      });
      setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
      setEditItem(null);
      toast.success('Saved');
      loadStats();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Select-all acts on the currently-filtered set, not the whole catalog.
  const isAllSelected =
    filteredItems.length > 0 && filteredItems.every((i) => selectedIds.has(i.id));
  const handleSelectAll = () => {
    setSelectedIds(isAllSelected ? new Set() : new Set(filteredItems.map(i => i.id)));
  };

  return (
    <Box sx={{ height: '100%', bgcolor: 'grey.50', overflow: 'auto' }}>
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/process')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <CatalogIcon sx={{ mr: 0.5 }} fontSize="small" />
            AI Master Data
          </Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar variant="rounded" sx={{ bgcolor: '#6a1b9a', width: 48, height: 48, mt: 0.5 }}>
            <CatalogIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight={800} gutterBottom>
              AI Master Data
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Import your product catalog and let AI assign HS codes, flag export-control status,
              and rewrite descriptions into customs-grade language.
            </Typography>
          </Box>
        </Box>

        {/* KPI row */}
        {stats && stats.total > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
              gap: 2,
              mb: 3,
            }}
          >
            {[
              { label: 'Total Products', value: stats.total, color: '#1976d2', Icon: TotalIcon },
              { label: 'Enriched', value: `${stats.enriched_pct}%`, color: '#2e7d32', Icon: EnrichedIcon },
              { label: 'Needs Review', value: stats.needs_review, color: '#ed6c02', Icon: ReviewIcon },
              { label: 'Dual-Use Flagged', value: stats.dual_use, color: '#d32f2f', Icon: DualUseIcon },
              { label: 'Open Alerts', value: alerts.length, color: '#6a1b9a', Icon: AlertsIcon },
            ].map(({ label, value, color, Icon }) => (
              <Paper
                key={label}
                elevation={0}
                sx={{
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderLeft: `4px solid ${color}`,
                  borderRadius: 2,
                  transition: 'box-shadow .2s, transform .2s',
                  '&:hover': { boxShadow: 3, transform: 'translateY(-2px)' },
                }}
              >
                <Avatar variant="rounded" sx={{ bgcolor: `${color}14`, color, width: 44, height: 44 }}>
                  <Icon />
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1.1 }}>
                    {value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {label}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        )}

        {/* Compliance Radar */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, borderColor: '#6a1b9a' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: alerts.length ? 2 : 0, flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RadarIcon sx={{ color: '#6a1b9a' }} />
              <Typography variant="h6" fontWeight={700}>
                Compliance Radar{alerts.length > 0 ? ` (${alerts.length} open)` : ''}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Watches your catalog for HS-code drift, newly dual-use products, and expiring origin proof.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetLearning}
                sx={{ borderColor: '#6a1b9a', color: '#6a1b9a' }}
              >
                Reset AI Learning
              </Button>
              <Button
                variant="contained"
                startIcon={radarRunning ? <CircularProgress size={16} color="inherit" /> : <RadarIcon />}
                onClick={handleRunRadar}
                disabled={radarRunning}
                sx={{ bgcolor: '#6a1b9a', '&:hover': { bgcolor: '#4a148c' } }}
              >
                {radarRunning ? 'Scanning...' : 'Run Radar Now'}
              </Button>
            </Box>
          </Box>

          {alerts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No open alerts. Run the radar to scan your confirmed products now.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {alerts.map((a) => (
                <Box
                  key={a.id}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 1, p: 1, borderRadius: 1,
                    bgcolor: a.severity === 'critical' ? '#fdecea' : a.severity === 'warning' ? '#fff8e1' : '#e3f2fd',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        label={a.severity}
                        size="small"
                        color={a.severity === 'critical' ? 'error' : a.severity === 'warning' ? 'warning' : 'info'}
                      />
                      <Typography variant="body2" fontWeight={600}>{a.message}</Typography>
                    </Box>
                    {a.product_description && (
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {a.product_description}
                      </Typography>
                    )}
                  </Box>
                  <Button size="small" startIcon={<AckIcon />} onClick={() => handleAcknowledge(a.id)}>
                    Acknowledge
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              component="label"
              startIcon={uploading ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
              disabled={uploading}
            >
              {uploading ? 'Importing...' : 'Import Catalog (CSV/Excel/PDF)'}
              <input type="file" hidden accept=".csv,.xlsx,.xls,.pdf" onChange={handleFileChange} />
            </Button>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
              Add Product
            </Button>
            {items.length > 0 && (
              <>
                <Button
                  variant="outlined"
                  startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                  onClick={() => handleExport('csv')}
                  disabled={exporting}
                >
                  Export CSV
                </Button>
                <Button
                  variant="outlined"
                  startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                  onClick={() => handleExport('excel')}
                  disabled={exporting}
                >
                  Export Excel
                </Button>
              </>
            )}
          </Box>

          {selectedIds.size > 0 && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={bulkEnriching ? <CircularProgress size={16} color="inherit" /> : <EnrichIcon />}
                onClick={handleBulkEnrich}
                disabled={bulkEnriching}
              >
                {bulkEnriching ? 'Enriching...' : `Enrich Selected (${selectedIds.size})`}
              </Button>
              <Button variant="outlined" color="success" onClick={handleBulkConfirm}>
                Confirm ({selectedIds.size})
              </Button>
              <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleBulkDelete}>
                Delete ({selectedIds.size})
              </Button>
            </Box>
          )}
        </Box>

        {/* Natural-language bulk command bar (Digicust-style) */}
        {selectedIds.size > 0 && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              fullWidth
              placeholder={`Tell AI what to do with ${selectedIds.size} selected product(s) — e.g. "classify these", "fill missing HS codes", "confirm all"`}
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleRunCommand(); }}
              disabled={running}
            />
            <Button
              variant="contained"
              startIcon={running ? <CircularProgress size={16} color="inherit" /> : <EnrichIcon />}
              onClick={handleRunCommand}
              disabled={running || !command.trim()}
            >
              {running ? 'Running...' : 'Run'}
            </Button>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Search + status filter bar */}
        {items.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search SKU, supplier, description or HS code…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 320, flex: '0 1 420px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {[
                { key: 'all', label: 'All', count: items.length },
                { key: 'needs_review', label: 'Needs Review', count: items.filter(i => i.status === 'needs_review').length },
                { key: 'enriched', label: 'Enriched', count: items.filter(i => i.status === 'enriched').length },
                { key: 'confirmed', label: 'Confirmed', count: items.filter(i => i.status === 'confirmed').length },
                { key: 'pending', label: 'Pending', count: items.filter(i => i.status === 'pending').length },
                { key: 'dual_use', label: 'Dual-Use', count: items.filter(i => i.is_dual_use === true).length },
              ].map(({ key, label, count }) => (
                <Chip
                  key={key}
                  label={`${label} (${count})`}
                  size="small"
                  onClick={() => setStatusFilter(key)}
                  color={statusFilter === key ? 'primary' : 'default'}
                  variant={statusFilter === key ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Alert severity="info">No products imported yet. Upload a CSV/Excel catalog to get started.</Alert>
        ) : filteredItems.length === 0 ? (
          <Alert severity="info" sx={{ display: 'flex', alignItems: 'center' }}>
            No products match your search / filter.
            <Button size="small" sx={{ ml: 1 }} onClick={() => { setSearch(''); setStatusFilter('all'); }}>
              Clear filters
            </Button>
          </Alert>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox checked={isAllSelected} onChange={handleSelectAll} />
                  </TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>HS Code</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Origin</TableCell>
                  <TableCell>Dual-Use</TableCell>
                  <TableCell>ECCN / AL</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                  <TableRow key={item.id} hover selected={selectedIds.has(item.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedIds.has(item.id)} onChange={() => handleSelectOne(item.id)} />
                    </TableCell>
                    <TableCell>{item.sku || '-'}</TableCell>
                    <TableCell>{item.supplier_name || '-'}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Typography variant="body2" noWrap title={item.enriched_description || item.raw_description}>
                        {item.enriched_description || item.raw_description}
                      </Typography>
                      {sourceLabel(item.source_module) && (
                        <Chip
                          label={sourceLabel(item.source_module)}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {item.hs_code
                        ? (
                          <Tooltip title="Why this classification?">
                            <Box
                              component="span"
                              onClick={() => setReasoningItem(item)}
                              sx={{
                                fontFamily: 'monospace', fontSize: '0.85rem',
                                color: 'primary.main', cursor: 'pointer',
                                textDecoration: 'underline dotted',
                                textUnderlineOffset: 3,
                              }}
                            >
                              {item.hs_code}
                            </Box>
                          </Tooltip>
                        )
                        : '-'}
                    </TableCell>
                    <TableCell>{confidenceChip(item.hs_code_confidence)}</TableCell>
                    <TableCell>{item.origin_country || '-'}</TableCell>
                    <TableCell>
                      {item.dual_use_check_failed
                        ? <Chip label="Check failed" size="small" color="default" variant="outlined" />
                        : <>
                            {item.is_dual_use === true && <Chip label="Dual-Use" size="small" color="error" />}
                            {item.is_dual_use === false && <Chip label="Clear" size="small" color="success" variant="outlined" />}
                            {item.is_dual_use == null && '-'}
                          </>}
                    </TableCell>
                    <TableCell>{item.dual_use_al_numbers || '-'}</TableCell>
                    <TableCell>
                      <Chip label={item.status} size="small" color={statusColor(item.status)} />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.25 }}>
                        <Tooltip title="Why this classification?">
                          <IconButton size="small" onClick={() => setReasoningItem(item)}>
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Enrich with AI">
                          <span>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleEnrichOne(item.id)}
                              disabled={enrichingId === item.id}
                            >
                              {enrichingId === item.id ? <CircularProgress size={16} /> : <EnrichIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Review & edit">
                          <IconButton size="small" onClick={() => openEdit(item)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={item.hs_code ? 'Estimate import duty & landed cost' : 'Classify an HS code first'}>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openDuty(item)}
                              disabled={!item.hs_code}
                            >
                              <DutyIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(item.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          </TableContainer>
        )}

        <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Review & Confirm</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {editItem?.raw_description}
            </Typography>
            <TextField
              fullWidth
              label="HS Code"
              value={editHsCode}
              onChange={(e) => setEditHsCode(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Customs-Grade Description"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditItem(null)}>Cancel</Button>
            <Button onClick={handleSaveEdit} variant="contained" disabled={saving}>
              {saving ? 'Saving...' : 'Save & Confirm'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add Product</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Product Description"
              required
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              sx={{ mb: 2, mt: 1 }}
            />
            <TextField
              fullWidth
              label="SKU (optional)"
              value={newSku}
              onChange={(e) => setNewSku(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Supplier (optional)"
              value={newSupplier}
              onChange={(e) => setNewSupplier(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAddProduct} variant="contained" disabled={adding || !newDescription.trim()}>
              {adding ? 'Adding...' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!dutyItem} onClose={() => setDutyItem(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Landed-Cost Estimator</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {dutyItem?.enriched_description || dutyItem?.raw_description}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              HS code {dutyItem?.hs_code} · duty rate from live EU TARIC
            </Typography>
            <TextField
              fullWidth
              type="number"
              label="Customs value (CIF, EUR)"
              helperText="Goods + freight + insurance to the EU border"
              value={dutyValue}
              onChange={(e) => setDutyValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !dutyLoading) handleEstimateDuty(); }}
              sx={{ mt: 2, mb: 1 }}
            />

            {dutyResult && !dutyResult.available && (
              <Alert severity="warning" sx={{ mt: 2 }}>{dutyResult.message}</Alert>
            )}

            {dutyResult && dutyResult.available && (
              <Box sx={{ mt: 2 }}>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Customs value</TableCell>
                      <TableCell align="right">€{dutyResult.customs_value?.toFixed(2)}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>
                        Duty rate {dutyResult.duty_pct != null ? `(${dutyResult.duty_pct}%)` : '(non-ad-valorem)'}
                      </TableCell>
                      <TableCell align="right">
                        {dutyResult.duty_amount != null ? `€${dutyResult.duty_amount.toFixed(2)}` : dutyResult.duty_rate_raw}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Import VAT ({dutyResult.vat_pct}%)</TableCell>
                      <TableCell align="right">
                        {dutyResult.vat_amount != null ? `€${dutyResult.vat_amount.toFixed(2)}` : '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Total landed cost</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {dutyResult.landed_cost != null ? `€${dutyResult.landed_cost.toFixed(2)}` : '—'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                {dutyResult.note && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    {dutyResult.note}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDutyItem(null)}>Close</Button>
            <Button onClick={handleEstimateDuty} variant="contained" disabled={dutyLoading || !dutyValue}>
              {dutyLoading ? 'Estimating...' : 'Estimate'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!reasoningItem} onClose={() => setReasoningItem(null)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EnrichIcon fontSize="small" color="secondary" />
            Why the AI chose this
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {reasoningItem?.enriched_description || reasoningItem?.raw_description}
            </Typography>
            {sourceLabel(reasoningItem?.source_module) && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Auto-added {sourceLabel(reasoningItem?.source_module)?.replace('From ', 'from ')}
                {reasoningItem?.source_reference ? ` · ${reasoningItem.source_reference}` : ''}
              </Typography>
            )}

            {/* HS code reasoning */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2">HS Code</Typography>
                {reasoningItem?.hs_code
                  ? <Chip size="small" label={reasoningItem.hs_code} sx={{ fontFamily: 'monospace' }} />
                  : <Chip size="small" label="not classified" variant="outlined" />}
                {confidenceChip(reasoningItem?.hs_code_confidence)}
              </Box>
              <Typography variant="body2" color={reasoningItem?.hs_code_reasoning ? 'text.primary' : 'text.secondary'}>
                {reasoningItem?.hs_code_reasoning || 'No reasoning recorded — run Enrich to generate one.'}
              </Typography>
            </Box>

            {/* Export-control (dual-use) reasoning */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2">Export Control</Typography>
                {reasoningItem?.dual_use_check_failed
                  ? <Chip size="small" label="check failed" variant="outlined" />
                  : reasoningItem?.is_dual_use === true
                    ? <Chip size="small" color="error" label={`Dual-Use${reasoningItem.dual_use_al_numbers ? ` · ${reasoningItem.dual_use_al_numbers}` : ''}`} />
                    : reasoningItem?.is_dual_use === false
                      ? <Chip size="small" color="success" variant="outlined" label="Clear" />
                      : <Chip size="small" label="not checked" variant="outlined" />}
              </Box>
              <Typography variant="body2" color={reasoningItem?.eccn_reasoning ? 'text.primary' : 'text.secondary'}>
                {reasoningItem?.eccn_reasoning || 'No dual-use reasoning recorded.'}
              </Typography>
            </Box>

            {/* Origin reasoning */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2">Origin</Typography>
                {reasoningItem?.origin_country
                  ? <Chip size="small" label={reasoningItem.origin_country} />
                  : <Chip size="small" label="not stated" variant="outlined" />}
              </Box>
              <Typography variant="body2" color={reasoningItem?.origin_reasoning ? 'text.primary' : 'text.secondary'}>
                {reasoningItem?.origin_reasoning || 'Origin is only recorded when the source explicitly states it — never guessed.'}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReasoningItem(null)}>Close</Button>
            {reasoningItem && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => { openEdit(reasoningItem); setReasoningItem(null); }}
              >
                Review & Edit
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default MasterDataPage;
